import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { IconClose } from "@veto/ui";

/**
 * Nested-modality: only the topmost modal responds to Escape. Each instance
 * registers on mount; window-level keydown fires for every listener, so the
 * stack check (not stopPropagation) is what scopes the close (FR-CON-002
 * scanner opens over the details sheet).
 */
const modalStack: symbol[] = [];

export type ModalSize = "sm" | "md" | "lg" | "sheet";

/**
 * Shared PWA overlay: Escape, backdrop tap, and header Close.
 * `sheet` pins to the bottom on narrow viewports (mobile/PWA).
 * Rendered through a portal so `position: fixed` is never trapped by a
 * backdrop-filter/transform ancestor (e.g. the frosted conversation pane).
 */
export function ModalShell({
  title,
  ariaLabel,
  onClose,
  children,
  size = "md",
  footer,
  hideClose = false,
  className = "",
}: {
  title?: string;
  ariaLabel: string;
  onClose: () => void;
  children: ReactNode;
  size?: ModalSize;
  footer?: ReactNode;
  hideClose?: boolean;
  className?: string;
}) {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Portal only after mount: server render (renderToStaticMarkup) rejects portals.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Parents pass a fresh `onClose` on every render: keep the latest in a ref so a re-render
  // never re-runs the mount effect (that re-focused the card mid-typing and moved this modal
  // back to the top of the Escape stack above a nested one).
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const entry = Symbol("modal");
    modalStack.push(entry);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (modalStack[modalStack.length - 1] !== entry) return;
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    window.addEventListener("keydown", onKey);
    // Focus the sheet for screen readers / keyboard users.
    cardRef.current?.focus({ preventScroll: true });
    return () => {
      const idx = modalStack.indexOf(entry);
      if (idx >= 0) modalStack.splice(idx, 1);
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  const sizeClass =
    size === "lg"
      ? "modal-card modal-card-lg"
      : size === "sm"
        ? "modal-card modal-card-sm"
        : size === "sheet"
          ? "modal-card modal-card-sheet"
          : "modal-card modal-card-md";

  const sheet = (
    <div
      className={`modal-backdrop${size === "sheet" ? " modal-backdrop-sheet" : ""}`}
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className={`card ${sizeClass} ${className}`.trim()}
        role="dialog"
        aria-modal
        aria-label={ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {size === "sheet" && <div className="modal-grabber" aria-hidden />}
        {(title || !hideClose) && (
          <header className="modal-head">
            {title ? (
              <h2 id={titleId} className="modal-title">
                {title}
              </h2>
            ) : (
              <span className="modal-title-spacer" />
            )}
            {!hideClose && (
              <button
                type="button"
                className="icon-btn modal-close"
                aria-label="Close"
                title="Close"
                onClick={onClose}
              >
                <IconClose />
              </button>
            )}
          </header>
        )}
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-footer">{footer}</footer> : null}
      </div>
    </div>
  );

  // SSR / pre-mount renders inline (same viewport position once fixed applies).
  if (!mounted) return sheet;
  return createPortal(sheet, document.body);
}
